import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { ACCEPTED_FILE_TYPES } from '@shared/utils/app-static-data';
import { IDocument } from 'app/models/document/document';
import { ATP_COURSE_DOCUMENT_TYPES, DOCUMENT_CATEGORIES } from 'app/models/document/document-categories';
import { DocumentService } from 'app/services/document/document.service';
import { AtpService } from 'app/services/atp/atp.service';
import { SharedService } from 'app/services/core/shared.service';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationDialogueComponent } from 'app/components/application/common/confirmation-dialogue/confirmation-dialogue.component';

@Component({
  selector: 'app-add-edit-atp-course-document',
  templateUrl: './add-edit-atp-course-document.component.html',
  styleUrls: ['./add-edit-atp-course-document.component.scss']
})
export class AddEditAtpCourseDocumentComponent implements OnInit, OnChanges {
  @Input() courseId: number = 0;
  @Input() cardColor: string = '';

  public tableColumns = ['fileName', 'documentCategory', 'createDate', 'actions'];
  public tableFormat = [
    { displayName: 'File Name', columnName: 'fileName', type: 'string', size: '40' },
    { displayName: 'Document Type', columnName: 'documentCategory', type: 'string', size: '30' },
    { displayName: 'Create Date', columnName: 'createDate', type: 'date', size: '20' }
  ];
  public tableData: IDocument[] = [];
  public tableDataFiltered: IDocument[] = [];
  public tablePageSize = 10;
  public filterTableString: string = '';
  public tableDataSource: MatTableDataSource<IDocument>;
  public loading: boolean = false;
  public uploading: boolean = false;
  public selectedDocumentType: string = DOCUMENT_CATEGORIES.preTrainingDocument;
  public documentTypes: string[] = ATP_COURSE_DOCUMENT_TYPES;
  public acceptedFileTypes = ACCEPTED_FILE_TYPES.displayText;

  @ViewChild('TablePaginator') tablePaginator: MatPaginator;
  @ViewChild('TableSort') tableSort: MatSort;

  constructor(
    public sharedService: SharedService,
    private readonly toastr: ToastrService,
    private readonly atpService: AtpService,
    private readonly documentService: DocumentService,
    public dialog: MatDialog
  ) { }

  ngOnInit(): void {
    if (this.courseId > 0) {
      this.loadDocuments();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['courseId'] && !changes['courseId'].firstChange && this.courseId > 0) {
      this.loadDocuments();
    }
  }

  loadDocuments(): void {
    if (this.courseId === 0) return;
    
    this.loading = true;
    this.documentService.getDocuments({ ownerType: 'AtpCourse', ownerId: this.courseId }).subscribe({
      next: (documents) => {
        this.tableData = documents;
        this.filterTable();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading documents:', error);
        this.toastr.error('Error loading documents: ' + (error.error || error.message));
        this.loading = false;
      }
    });
  }

  filterTable(): void {
    this.tableDataFiltered = this.tableData;

    if (this.filterTableString) {
      this.tableDataFiltered = this.tableDataFiltered.filter(x => {
        return x.fileName?.toLowerCase().includes(this.filterTableString.toLowerCase()) ||
          x.documentCategory?.toLowerCase().includes(this.filterTableString.toLowerCase());
      });
    }

    this.updateTableDataSource();
  }

  updateTableDataSource(): void {
    this.tableDataSource = new MatTableDataSource<IDocument>(this.tableDataFiltered);
    this.tableDataSource.paginator = this.tablePaginator;
    this.tableDataSource.sort = this.tableSort;
  }

  get documentCount(): number {
    return this.tableData?.length || 0;
  }

  get isLoading(): boolean {
    return this.loading;
  }

  triggerFileInput(): void {
    const fileInput = document.getElementById('courseDocumentFileInput') as HTMLInputElement;
    fileInput?.click();
  }

  onFileSelected(event: any): void {
    const file: File = event.target.files[0];
    if (file) {
      this.uploading = true;
      this.documentService.uploadDocument(file, { ownerType: 'AtpCourse', ownerId: this.courseId }, this.selectedDocumentType).subscribe({
        next: () => {
          this.toastr.success('Document uploaded successfully');
          this.uploading = false;
          this.loadDocuments();
          // Reset file input
          const fileInput = document.getElementById('courseDocumentFileInput') as HTMLInputElement;
          if (fileInput) fileInput.value = '';
        },
        error: (error) => {
          console.error('Upload error:', error);
          this.toastr.error('Upload failed: ' + (error.error || error.message));
          this.uploading = false;
        }
      });
    }
  }

  downloadFile(document: IDocument): void {
    this.documentService.downloadDocument(document).subscribe({
      error: (error) => {
        console.error('Download error:', error);
        this.toastr.error('Download failed: ' + (error.error || error.message));
      }
    });
  }

  deleteFile(document: IDocument): void {
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: { 
        message: `Are you sure you want to <strong>Delete</strong> the document <strong>${document.fileName}</strong>?`, 
        title: 'Delete Document' 
      },});

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.documentService.deleteDocument(document.id).subscribe({
          next: () => {
            this.toastr.success('Document deleted successfully');
            this.loadDocuments();
          },
          error: (error) => {
            console.error('Delete error:', error);
            this.toastr.error('Delete failed: ' + (error.error || error.message));
          }
        });
      }
    });
  }
}
